import type { DraftLineItem, FinancialSummary, Order, OrderLineItem } from "../types";

function getLineItemUnitPrice(
  order: Order,
  lineItemId: string,
  { includeTaxes = false }: { includeTaxes?: boolean } = {},
): number {
  const lineItem = order.lineItems?.find(
    (li: OrderLineItem) => li.line_item_id === lineItemId,
  );
  const price = parseFloat((lineItem as any)?.price ?? "0");
  if (isNaN(price)) return 0;

  let taxPerUnit = 0;
  if (includeTaxes) {
    const quantity = (lineItem as any)?.quantity ?? 1;
    const taxLines: { price?: string }[] = (lineItem as any)?.tax_lines ?? [];
    const totalTax = taxLines.reduce((sum, tl) => sum + parseFloat(tl.price ?? "0"), 0);
    taxPerUnit = quantity > 0 ? totalTax / quantity : 0;
  }

  return (price + taxPerUnit) * 100;
}

export function calculateTotals(
  lineItems: DraftLineItem[],
  order: Order,
): FinancialSummary {
  const currency = (order.currency as any)?.shopCurrency ?? "EUR";

  let refundTotal = 0;
  let exchangeTotal = 0;
  let exchangeVariantTotal = 0; // only variant swaps — drives amountDue
  let discountTotal = 0;

  for (const item of lineItems) {
    const unitPrice = getLineItemUnitPrice(order, item.lineItemId);
    const itemTotal = unitPrice * item.quantity;

    switch (item.resolution) {
      case "refund":
        refundTotal += itemTotal;
        break;
      case "discount":
        discountTotal += itemTotal;
        break;
      case "exchange":
        exchangeTotal += itemTotal;
        exchangeVariantTotal += itemTotal;
        break;
      case "exchange-credit":
        // Include taxes: credit given to customer is based on what they originally paid (TTC)
        exchangeTotal += getLineItemUnitPrice(order, item.lineItemId, { includeTaxes: true }) * item.quantity;
        break;
    }
  }

  // exchange-credit is store credit spent later — not a payment due now
  const amountDue = Math.max(0, exchangeVariantTotal - refundTotal - discountTotal);

  return { refundTotal, exchangeTotal, discountTotal, amountDue, currency };
}
